import { Box, Chip, Skeleton, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { BookingRulesType } from 'src/features/booking-rules/api/types/response/booking-rule.response';
import { bookingRulesTypeTranslations } from 'src/features/booking-rules/constants/booking-rules-type-translations';
import { useEmployeesGroupsByTokens } from 'src/features/employees-groups/api/hooks/use-employees-groups-by-tokens';
import { HandleChangeFilterValue } from 'src/types/handle-change-filter-value';
import { Filters } from 'src/types/interfaces/filters';
import { toggleFilterItemSelection } from 'src/utils/filters/toggle-filter-item-selection';

interface Props {
  filters: Filters;
  handleChangeFilterValue: HandleChangeFilterValue;
}

export const BookingRulesFilterChips = ({
  filters,
  handleChangeFilterValue,
}: Props) => {
  const { t } = useTranslation('bookingRules');

  const { results } = useEmployeesGroupsByTokens(
    (filters?.['assignedEmployeeGroups.token']?.value as string[]) ?? [],
  );

  const getEmployeesGroupName = (token: string) => {
    const employeeGroup = results?.hits?.find(
      employeeGroup => employeeGroup.token === token,
    );

    return employeeGroup?.name;
  };

  const renderFilterChips = () =>
    Object.entries(filters)?.map(
      ([filterType, { value: filterValues, operator }]) => {
        if (!filterValues) {
          return null;
        }

        if (Array.isArray(filterValues)) {
          return filterValues?.map(value => {
            switch (filterType) {
              case 'durationType':
                return (
                  <Chip
                    key={value.toString()}
                    sx={{ mr: 1, mb: 1 }}
                    color="primary"
                    label={
                      <Typography
                        variant="caption"
                        sx={{ textTransform: 'capitalize' }}>
                        {`${t('common.columns.durationType')}: ${t(bookingRulesTypeTranslations[value as BookingRulesType])}`}
                      </Typography>
                    }
                    size="small"
                    onDelete={() => {
                      toggleFilterItemSelection(
                        filterType,
                        value.toString(),
                        false,
                        filters,
                        handleChangeFilterValue,
                        operator,
                      );
                    }}
                  />
                );

              case 'assignedEmployeeGroups.token':
                if (!results?.hits) {
                  return <Skeleton />;
                }

                return (
                  <Chip
                    key={value.toString()}
                    sx={{ mr: 1, mb: 1 }}
                    color="primary"
                    label={
                      <Typography
                        variant="caption"
                        sx={{ textTransform: 'capitalize' }}>
                        {`${t('common.columns.employeeGroups')}: ${getEmployeesGroupName(value.toString())}`}
                      </Typography>
                    }
                    size="small"
                    onDelete={() => {
                      toggleFilterItemSelection(
                        filterType,
                        value.toString(),
                        false,
                        filters,
                        handleChangeFilterValue,
                        operator,
                      );
                    }}
                  />
                );

              default:
                return (
                  <Chip
                    key={value.toString()}
                    sx={{ mr: 1, mb: 1 }}
                    color="primary"
                    label={
                      <Typography
                        variant="caption"
                        sx={{ textTransform: 'capitalize' }}>
                        {value}
                      </Typography>
                    }
                    size="small"
                    onDelete={() => {
                      toggleFilterItemSelection(
                        filterType,
                        value.toString(),
                        false,
                        filters,
                        handleChangeFilterValue,
                        operator,
                      );
                    }}
                  />
                );
            }
          });
        }
      },
    );

  return (
    <Box
      sx={{
        alignItems: 'center',
        display: 'flex',
        justifyItems: 'flex-start',
        flexWrap: 'wrap',
        px: 3,
        pb: 1.5,
      }}>
      {renderFilterChips()}
    </Box>
  );
};
